import type { DataResponse } from "@/types";
import { api } from "../api";

export interface AvailableIntegration {
  slug: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  credentialType: string; // "oauth" | "api_key" | ...
  connected: boolean;
  accountLabel?: string | null;
  expiresAt?: string | null;
}

interface AuthorizeResponse {
  authorizeUrl: string;
}

export type AccessPermission = "read" | "write";

export interface AccessPermissionConsent {
  present: boolean;
  consentMode: "opt_in" | "opt_out";
  effectiveEnabled: boolean;
  hasExplicitPreference: boolean;
}

export interface ExtensionConsent {
  slug: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  extensionType: string;
  read: AccessPermissionConsent;
  write: AccessPermissionConsent;
}

/**
 * Per-user extension credentials (MHS-449). Connect/disconnect a user's own
 * account for extensions that require per-user auth (Asana OAuth now; per-user
 * API keys / other methods later). Keyed by extension slug.
 */
export const integrationsService = {
  /** Enabled per-user-credential extensions the user can access, with status. */
  listAvailable: () => api.get<AvailableIntegration[]>("/integrations/available"),

  /** Get the OAuth authorize URL to start connecting an extension. */
  getAuthorizeUrl: (slug: string) => api.get<AuthorizeResponse>(`/integrations/${slug}/authorize`),

  /** Revoke + delete the user's credential for an extension. */
  disconnect: (slug: string) => api.delete(`/integrations/${slug}/connection`),

  /** Integration extensions the user can access, with per-user read/write consent (MHS-588). */
  listExtensionConsent: () => api.get<DataResponse<ExtensionConsent[]>>("/integrations/extensions"),

  /** Save the user's read or write choice for an extension. */
  setExtensionPreference: (slug: string, permission: AccessPermission, enabled: boolean) =>
    api.put<DataResponse<ExtensionConsent>>(`/integrations/extensions/${slug}/preference`, { permission, enabled }),

  /** Remove the user's choice so the extension's default applies again. */
  clearExtensionPreference: (slug: string, permission: AccessPermission) =>
    api.delete(`/integrations/extensions/${slug}/preference?permission=${permission}`),
};
